/*
 * moasure-pdp-runtime.js
 * Shared by the Moasure 2 and Moasure 2 PRO PDPs (one runtime, both products, monolith or sectioned).
 * Native replacement for Instant's `instant-core.min.js` + Swiper on the Moasure 2 PDP.
 *
 * It reads the SAME `data-instant-*` attributes and slider-params JSON the markup already carries, and drives the
 * SAME classes/state attributes the stylesheet responds to — so the page looks and behaves identically with or
 * without the Instant app installed.
 *
 * COEXISTENCE: while the Instant app embed is installed, instant-core auto-initializes this markup. Running both
 * would double-bind, so this runtime STANDS DOWN whenever instant-core actually ran, and only takes over when it
 * did not (app uninstalled, or blocked). Note: the app embed sets window.Instant.initialized/initializedAppEmbed
 * in an inline <head> script even when instant-core never loads — those flags are NOT evidence and are never used.
 *
 * Derived from assets/moasure-lx1-runtime.js. This page additionally needs: slidesPerView:"auto" sliders (3 of the
 * 7), Swiper's dynamicBullets pagination (1 of 5), watchOverflow locking (all 7), a logos ticker, and
 * scroll-to-section jump links.
 *
 * Defensive throughout (never throws), idempotent (global + per-element guards).
 */
(function () {
  'use strict';
  if (window.__mpdpInit) return;
  window.__mpdpInit = true;

  var ROOT_SEL = '[data-mpdp-native]';
  var DISABLED = 'instant-slider-button-disabled';

  function $all(sel, ctx) { try { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); } catch (e) { return []; } }
  function safe(fn) { return function () { try { return fn.apply(this, arguments); } catch (e) { /* swallow */ } }; }
  function closest(el, sel) { while (el && el.nodeType === 1) { if (el.matches && el.matches(sel)) return el; el = el.parentElement; } return null; }
  function bound(el, key) { key = key || '__mpdpBound'; if (!el || el[key]) return true; el[key] = true; return false; }
  function kids(el, cls) {
    var out = [];
    if (!el) return out;
    for (var i = 0; i < el.children.length; i++) if (!cls || el.children[i].classList.contains(cls)) out.push(el.children[i]);
    return out;
  }
  function num(v) { var n = parseFloat(v); return isNaN(n) ? 0 : n; }

  /* ================= sliders ================= */
  var mains = {};    // sliderId -> api (data-instant-type="slider")
  var thumbs = {};   // sliderId -> api (data-instant-type="thumbnails")
  var allApis = [];

  function readParams(el) {
    try { var s = document.getElementById('instant-slider-' + el.id + '-params'); return s ? JSON.parse(s.textContent) : null; } catch (e) { return null; }
  }
  function breakpoint(params) {
    if (!params || !params.breakpoints) return {};
    var w = window.innerWidth, best = null, bestK = -1;
    for (var k in params.breakpoints) {
      var n = parseInt(k, 10);
      if (w >= n && n > bestK) { bestK = n; best = params.breakpoints[k]; }
    }
    return best || {};
  }

  function initSlider(el) {
    if (bound(el)) return null;
    var wrapper = el.querySelector('.instant-slider-wrapper');
    if (!wrapper) return null;
    var slides = kids(wrapper, 'instant-slider-slide');
    if (!slides.length) return null;

    var params = readParams(el) || {};
    var container = closest(el, '.instant-slider-container') || el.parentElement || document;
    var sid = el.getAttribute('data-instant-slider-id');
    var isThumbs = el.getAttribute('data-instant-type') === 'thumbnails';
    var pagCfg = params.pagination || null;
    var dynamic = !!(pagCfg && pagCfg.dynamicBullets);
    var watchOverflow = params.watchOverflow !== false;

    var idx = 0, gap = 0, spv = 1, auto = false, centered = false;
    var offsets = [0], snaps = 1, maxIdx = 0, slideW = 0, locked = false, dormant = true;
    var pagEl = null, bulletClass = '', bullets = [];

    el.classList.add('instant-slider-initialized', 'instant-slider-horizontal', 'instant-slider-backface-hidden');
    if (params.watchSlidesProgress) el.classList.add('instant-slider-watch-progress');
    el.setAttribute('data-instant-slider-initialized', 'true');

    function navEls(sel) {
      if (!sel) return [];
      var els = $all(sel, container);
      return els.length ? els : $all(sel, document);
    }
    function offset(i) { return offsets[Math.max(0, Math.min(i, offsets.length - 1))] || 0; }

    // Resolve the pagination element and learn the hashed bullet class from the markup's seed bullet. Must run
    // BEFORE anything empties that element, or re-rendered bullets would lose the hash the stylesheet keys on.
    function resolvePag() {
      if (pagEl || !pagCfg || !pagCfg.el || pagCfg.enabled === false) return pagEl;
      pagEl = container.querySelector(pagCfg.el) || document.querySelector(pagCfg.el);
      if (!pagEl) return null;
      var first = pagEl.firstElementChild;
      if (first) {
        bulletClass = first.className.split(/\s+/).filter(function (c) {
          return c && c.indexOf('instant-slider-pagination-bullet') !== 0;
        }).join(' ');
      }
      pagEl.classList.add('instant-slider-pagination-clickable', 'instant-slider-pagination-bullets', 'instant-slider-pagination-horizontal');
      if (dynamic) pagEl.classList.add('instant-slider-pagination-bullets-dynamic');
      return pagEl;
    }

    function layout() {
      var b = breakpoint(params);
      var rawSpv = b.slidesPerView;
      auto = rawSpv === 'auto';
      spv = auto ? 1 : (rawSpv || 1);
      gap = b.spaceBetween || 0;
      centered = !!b.centeredSlides;

      var W = el.clientWidth;
      // A slider inside a closed tab pane / accordion has clientWidth 0 — Swiper leaves those alone (no widths)
      // until they become visible, but it DOES empty the markup's seed bullet. Mirror both, and re-run layout when
      // the box appears (ResizeObserver).
      dormant = W <= 0;
      resolvePag();
      if (dormant) {
        if (pagEl && pagEl.children.length) { pagEl.innerHTML = ''; bullets = []; }
        return;
      }

      var i, prevSnaps = snaps;
      // Who decides slide width? Measured against the live page at all four widths:
      //   - slidesPerView:"auto"  → the stylesheet does (Swiper writes no inline width)
      //   - the THUMBNAILS strip  → the stylesheet does too, even with a numeric slidesPerView. Instant's CSS says
      //     calc(25% - 9.5px) = 80px at 390, and Swiper writes exactly that; the generic formula would give 80.5px,
      //     and that 0.5px makes the thumb images 0.38px taller, which shifts the whole page below the hero.
      //   - every other slider    → Swiper's formula wins (e.g. 512px at 1024 where the CSS says 511.672px).
      var cssOwnsWidth = auto || isThumbs;
      if (cssOwnsWidth) {
        for (i = 0; i < slides.length; i++) { slides[i].style.width = ''; slides[i].style.marginRight = gap ? gap + 'px' : ''; }
      } else {
        slideW = spv === 1 ? W : (W - (spv - 1) * gap) / spv;
        for (i = 0; i < slides.length; i++) {
          slides[i].style.width = slideW + 'px';
          slides[i].style.marginRight = gap ? gap + 'px' : '';
        }
      }
      // Offsets always come from measured geometry, then get clamped to maxTranslate the way Swiper does.
      var acc = 0, raw = [];
      for (i = 0; i < slides.length; i++) {
        raw.push(acc);
        acc += slides[i].offsetWidth + num(getComputedStyle(slides[i]).marginRight);
      }
      var maxOffset = Math.max(0, wrapper.scrollWidth - W);
      offsets = raw.map(function (o) { return Math.min(o, maxOffset); });
      slideW = slides[0] ? slides[0].offsetWidth : 0;
      if (auto) {
        // snap count = distinct reachable positions
        var seen = {}, uniq = 0;
        for (i = 0; i < offsets.length; i++) { var k = Math.round(offsets[i]); if (!(k in seen)) { seen[k] = 1; uniq++; } }
        snaps = Math.max(1, uniq);
      } else {
        snaps = centered ? slides.length : Math.max(1, slides.length - spv + 1);
      }
      maxIdx = snaps - 1;

      // watchOverflow: nothing to scroll → Swiper locks nav + pagination
      locked = watchOverflow && maxIdx <= 0;
      el.classList.toggle('instant-slider-locked', locked);
      var nav = params.navigation;
      if (nav) {
        navEls(nav.prevEl).concat(navEls(nav.nextEl)).forEach(function (b2) { b2.classList.toggle('instant-slider-button-lock', locked); });
      }

      if (snaps !== prevSnaps || !bullets.length) renderBullets();
      if (idx > maxIdx) idx = maxIdx;
    }

    function updateNav() {
      var nav = params.navigation;
      if (!nav) return;
      navEls(nav.prevEl).forEach(function (b) { b.classList.toggle(DISABLED, idx <= 0); });
      navEls(nav.nextEl).forEach(function (b) { b.classList.toggle(DISABLED, idx >= maxIdx); });
    }

    function updateBullets() {
      for (var i = 0; i < bullets.length; i++) {
        var b = bullets[i], d = i - idx;
        b.classList.toggle('instant-slider-pagination-bullet-active', i === idx);
        if (!dynamic) continue;
        // Swiper's dynamicBullets class ring (observed on the live page: -active-main / -next / -next-next)
        b.classList.toggle('instant-slider-pagination-bullet-active-main', d === 0);
        b.classList.toggle('instant-slider-pagination-bullet-active-next', d === 1);
        b.classList.toggle('instant-slider-pagination-bullet-active-next-next', d === 2);
        b.classList.toggle('instant-slider-pagination-bullet-active-prev', d === -1);
        b.classList.toggle('instant-slider-pagination-bullet-active-prev-prev', d === -2);
      }
    }

    function renderBullets() {
      if (!resolvePag()) return;
      pagEl.classList.toggle('instant-slider-pagination-lock', locked);
      pagEl.innerHTML = '';
      bullets = [];
      for (var i = 0; i < snaps; i++) {
        var b = document.createElement('span');
        b.className = 'instant-slider-pagination-bullet' + (bulletClass ? ' ' + bulletClass : '');
        b.addEventListener('click', safe((function (k) { return function () { go(k, true); }; })(i)));
        pagEl.appendChild(b);
        bullets.push(b);
      }
      updateBullets();
    }

    function go(i, animate) {
      i = Math.max(0, Math.min(i, maxIdx));
      idx = i;
      wrapper.style.transitionDuration = animate === false ? '0ms' : '300ms';
      wrapper.style.transform = 'translate3d(-' + offset(i) + 'px, 0px, 0px)';
      for (var n = 0; n < slides.length; n++) {
        slides[n].classList.toggle('instant-slider-slide-active', n === i);
        slides[n].classList.toggle('instant-slider-slide-prev', n === i - 1);
        slides[n].classList.toggle('instant-slider-slide-next', n === i + 1);
      }
      updateNav(); updateBullets();
      if (!isThumbs && sid && thumbs[sid]) thumbs[sid].follow(i);
    }

    // thumbnails: the thumb at the MAIN slider's index carries `instant-slider-thumb-active` (the class the CSS lifts).
    function follow(mainIdx) {
      for (var n = 0; n < slides.length; n++) slides[n].classList.toggle('instant-slider-thumb-active', n === mainIdx);
      var perView = auto ? Math.max(1, Math.floor(el.clientWidth / Math.max(1, slideW + gap))) : spv;
      if (mainIdx < idx || mainIdx > idx + perView - 1) go(Math.min(mainIdx, Math.max(0, slides.length - perView)), true);
    }

    var nav0 = params.navigation;
    if (nav0) {
      navEls(nav0.nextEl).forEach(function (b) { if (!bound(b, '__mpdpNav')) b.addEventListener('click', safe(function (e) { e.preventDefault(); go(idx + 1, true); })); });
      navEls(nav0.prevEl).forEach(function (b) { if (!bound(b, '__mpdpNav')) b.addEventListener('click', safe(function (e) { e.preventDefault(); go(idx - 1, true); })); });
    }

    /* drag / swipe */
    var pid = null, startX = 0, startT = 0, base = 0, dragging = false, downTarget = null, taps = [];
    el.addEventListener('dragstart', function (e) { e.preventDefault(); });
    el.addEventListener('pointerdown', safe(function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (locked) return;
      pid = e.pointerId; startX = e.clientX; startT = Date.now(); base = -offset(idx); dragging = false; downTarget = e.target;
      // NB: do NOT preventDefault() here — on pointerdown that suppresses the compatibility click event, which
      // would kill clicks on children inside the slider (the thumbnails). Native image-drag is stopped by the
      // dragstart handler above, and text selection by user-select while dragging.
      try { el.setPointerCapture(pid); } catch (err) {}
    }));
    el.addEventListener('pointermove', safe(function (e) {
      if (pid === null || e.pointerId !== pid) return;
      var dx = e.clientX - startX;
      if (!dragging && Math.abs(dx) <= 5) return;
      dragging = true;
      wrapper.style.transitionDuration = '0ms';
      wrapper.style.userSelect = 'none';
      var pos = base + dx, min = -offset(maxIdx), max = 0;
      if (pos > max) pos = max + (pos - max) * 0.3;
      if (pos < min) pos = min + (pos - min) * 0.3;
      wrapper.style.transform = 'translate3d(' + pos + 'px, 0px, 0px)';
    }));
    function endDrag(e) {
      if (pid === null || (e && e.pointerId !== undefined && e.pointerId !== pid)) return;
      try { el.releasePointerCapture(pid); } catch (err) {}
      pid = null;
      wrapper.style.userSelect = '';
      if (!dragging) {
        // a plain tap: setPointerCapture retargeted the compatibility click to the slider, so resolve the slide
        // from the element recorded at pointerdown and fire the tap handlers ourselves.
        if (taps.length && downTarget) {
          var slide = closest(downTarget, '.instant-slider-slide');
          var n = slide ? slides.indexOf(slide) : -1;
          if (n >= 0) taps.forEach(function (fn) { safe(fn)(n); });
        }
        return;
      }
      dragging = false;
      var dx = (e && e.clientX !== undefined ? e.clientX : startX) - startX;
      var dt = Math.max(1, Date.now() - startT);
      if (Math.abs(dx) > slideW * 0.2 || Math.abs(dx) / dt > 0.3) go(idx - (dx > 0 ? 1 : -1), true); else go(idx, true);
      el.__mpdpSuppressClick = true;
      setTimeout(function () { el.__mpdpSuppressClick = false; }, 300);
    }
    el.addEventListener('pointerup', safe(endDrag));
    el.addEventListener('pointercancel', safe(function () { if (pid !== null) { try { el.releasePointerCapture(pid); } catch (err) {} pid = null; dragging = false; wrapper.style.userSelect = ''; go(idx, true); } }));
    el.addEventListener('click', function (e) { if (el.__mpdpSuppressClick) { e.preventDefault(); e.stopPropagation(); } }, true);

    var api = {
      el: el, go: go, layout: layout, follow: follow,
      index: function () { return idx; }, isDormant: function () { return dormant; },
      onTap: function (fn) { taps.push(fn); },
      relayout: function () { var was = dormant; layout(); if (was && !dormant) go(Math.min(idx, maxIdx), false); },
    };
    if (sid) (isThumbs ? thumbs : mains)[sid] = api;
    allApis.push(api);
    el.__mpdpSlider = api;   // so other modules (variant pickers) can drive this slider in native mode

    layout();
    go(0, false);

    // Sliders that start inside a closed pane/accordion get laid out the moment they are revealed.
    if (window.ResizeObserver) {
      try {
        var ro = new ResizeObserver(safe(function () { if (dormant && el.clientWidth > 0) api.relayout(); }));
        ro.observe(el);
      } catch (e) {}
    }
    // auto-width slides depend on image intrinsic size: re-measure once images finish
    if (auto) {
      $all('img', el).forEach(function (img) {
        if (img.complete) return;
        img.addEventListener('load', safe(function () { api.relayout(); }), { once: true });
      });
    }
    return api;
  }

  function initSliders(root) {
    $all('[data-instant-type="slider"]', root).forEach(function (el) { safe(initSlider)(el); });
    $all('[data-instant-type="thumbnails"]', root).forEach(function (el) {
      var api = safe(initSlider)(el);
      var sid = el.getAttribute('data-instant-slider-id');
      var main = sid && mains[sid];
      if (!api || !main) return;
      api.onTap(function (n) { main.go(n, true); });
      api.follow(main.index());
    });
  }

  /* ================= accordions ================= */
  function contentOf(item) { return item.querySelector('[data-instant-type="accordion-content"]'); }
  function setAcc(item, open) {
    var c = contentOf(item);
    if (c) {
      c.style.setProperty('--instant-accordion-content-width', c.clientWidth + 'px');
      c.style.setProperty('--instant-accordion-content-height', open ? c.scrollHeight + 'px' : '0px');
    }
    item.setAttribute('data-state', open ? 'open' : 'closed');
    // a slider revealed by opening an accordion item needs a layout pass
    if (open) setTimeout(safe(function () { allApis.forEach(function (a) { if (a.isDormant()) a.relayout(); }); }), 60);
  }
  function initAccordions(root) {
    $all('[data-instant-type="accordion-container"]', root).forEach(function (container) {
      if (bound(container, '__mpdpAcc')) return;
      var items = $all('[data-instant-type="accordion-item"]', container).filter(function (i) { return closest(i, '[data-instant-type="accordion-container"]') === container; });
      var multi = container.getAttribute('data-is-multi-open-enabled') === 'true';
      items.forEach(function (item) {
        var c = contentOf(item);
        // An item the markup ships open (spec-tabs: "What's in the kit") keeps its natural height — measuring it
        // here would read 0 on desktop, where the whole accordion is display:none until the mobile breakpoint.
        var startsOpen = item.getAttribute('data-state') === 'open';
        if (c) { c.style.setProperty('--instant-accordion-content-height', startsOpen ? 'auto' : '0'); c.style.setProperty('--instant-accordion-content-width', c.clientWidth + 'px'); }
        if (!startsOpen) item.setAttribute('data-state', 'closed');
        var header = item.querySelector('[data-instant-type="accordion-header"]');
        if (!header || bound(header, '__mpdpAccH')) return;
        header.addEventListener('click', safe(function (e) {
          e.preventDefault(); e.stopPropagation();
          var open = item.getAttribute('data-state') !== 'open';
          if (open && !multi) items.forEach(function (o) { if (o !== item) setAcc(o, false); });
          setAcc(item, open);
        }));
      });
    });
  }

  /* ================= overlays ================= */
  var savedOverflow = null;
  function overlayFor(id) { return document.getElementById('i' + id) || document.getElementById(id); }
  function openOverlay(o) {
    if (!o || o.getAttribute('data-state') === 'open') return;
    if (savedOverflow === null) savedOverflow = document.body.style.overflow;
    o.setAttribute('data-state', 'open');
    document.body.style.overflow = 'hidden';
    var f = o.querySelector('button');
    if (f) { try { f.focus({ preventScroll: true }); } catch (e) {} }
  }
  function closeOverlay(o) {
    if (!o) return;
    o.setAttribute('data-state', 'closed');
    $all('video', o).forEach(function (v) { try { v.pause(); } catch (e) {} });
    if (!document.querySelector('[data-instant-type="overlay"][data-state="open"]')) {
      document.body.style.overflow = savedOverflow || '';
      savedOverflow = null;
    }
  }
  function initOverlays(root) {
    $all('[data-instant-action-type="open-overlay"]', root).forEach(function (btn) {
      if (bound(btn, '__mpdpOv')) return;
      btn.addEventListener('click', safe(function (e) {
        // triggers are <button>s with no type inside the root <form> — without this they submit and reload
        e.preventDefault(); e.stopPropagation();
        var id = btn.getAttribute('data-instant-action-id');
        if (!id) { closeOverlay(closest(btn, '[data-instant-type="overlay"]')); return; }
        var o = overlayFor(id);
        if (!o) return;
        if (closest(btn, '[data-instant-type="overlay"]') === o && o.getAttribute('data-state') === 'open') closeOverlay(o);
        else openOverlay(o);
      }));
    });
    $all('[data-instant-type="overlay"]', root).forEach(function (o) {
      if (bound(o, '__mpdpOvRoot')) return;
      o.addEventListener('pointerdown', safe(function (e) { if (e.target === o) closeOverlay(o); }));
    });
    if (!window.__mpdpEsc) {
      window.__mpdpEsc = true;
      document.addEventListener('keydown', safe(function (e) {
        if (e.key !== 'Escape') return;
        var open = $all('[data-instant-type="overlay"][data-state="open"]');
        if (open.length) closeOverlay(open[open.length - 1]);
      }));
    }
  }

  /* ================= scroll-to-section jump links ================= */
  function initScrollTo(root) {
    $all('[data-instant-action-type="scroll-to-section"]', root).forEach(function (el) {
      if (bound(el, '__mpdpJump')) return;
      el.addEventListener('click', safe(function (e) {
        var id = el.getAttribute('data-instant-action-id') || (el.getAttribute('href') || '').replace('#', '');
        var target = document.getElementById('i' + id) || document.getElementById(id);
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }));
    });
  }

  /* ================= scroll triggers ================= */
  function initScrollTriggers(root) {
    var els = $all('.instant-scroll-trigger', root).filter(function (el) { return !bound(el, '__mpdpScroll'); });
    if (!els.length) return;
    var ticking = false;
    function evaluate() {
      ticking = false;
      var y = window.pageYOffset || document.documentElement.scrollTop || 0;
      els.forEach(function (el) {
        var offset = parseInt(el.getAttribute('data-instant-scroll-into-view-offset') || '0', 10) || 0;
        var replay = el.getAttribute('data-instant-scroll-into-view-replay') === 'true';
        var shown = y >= offset;
        if (!shown && !replay && el.__mpdpShown) return;
        if (shown) el.__mpdpShown = true;
        el.classList.toggle('instant-scroll-trigger--hidden', !shown);
      });
    }
    window.addEventListener('scroll', function () { if (!ticking) { ticking = true; window.requestAnimationFrame(evaluate); } }, { passive: true });
    evaluate();
  }

  /* ================= ticker ================= */
  // The logos marquee. instant-core drives it with a CSS keyframe animation it injects; the stylesheet already
  // ships the keyframes (`instant-ticker-instant-scroll-…`), so we only have to duplicate the children until the
  // track is wide enough to loop and start the animation.
  function initTickers(root) {
    $all('[data-instant-type="ticker"]', root).forEach(function (host) {
      if (bound(host, '__mpdpTicker')) return;
      var track = host.querySelector('.instant-ticker') || host;
      var seed = track.querySelector('.instant-ticker-initial-child-container') || track.firstElementChild;
      if (!seed) return;
      var need = host.clientWidth * 2;
      var guard = 0;
      while (track.scrollWidth < need && guard++ < 12) track.appendChild(seed.cloneNode(true));
      var dur = num(getComputedStyle(track).animationDuration) || 32;
      if (!track.style.animationName && getComputedStyle(track).animationName === 'none') {
        // no keyframes reached this element — fall back to a rAF translate so the marquee still moves
        var w = seed.offsetWidth || track.scrollWidth / 2, x = 0, last = 0;
        var speed = w / Math.max(1, dur);   // px per second
        var step = function (t) {
          if (document.hidden) { last = t; return requestAnimationFrame(step); }
          if (last) { x -= speed * (t - last) / 1000; if (w && -x >= w) x += w; track.style.transform = 'translate3d(' + x + 'px,0,0)'; }
          last = t;
          requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    });
  }

  /* ================= tabs ================= */
  function initTabs(root) {
    $all('[data-instant-type="tabs-container"]', root).forEach(function (container) {
      if (bound(container, '__mpdpTabs')) return;
      var content = $all('[data-instant-type="tabs-content"]', container).filter(function (c) { return closest(c, '[data-instant-type="tabs-container"]') === container; })[0];
      var triggers = $all('[data-instant-type="tabs-trigger"]', container).filter(function (t) { return closest(t, '[data-instant-type="tabs-container"]') === container; });
      if (!content || !triggers.length) return;
      var panes = kids(content).filter(function (p) { return p.getAttribute('data-instant-type') === 'tabs-pane'; });
      function activate(tabId) {
        triggers.forEach(function (t) { t.setAttribute('data-instant-state', t.getAttribute('data-instant-tab-id') === tabId ? 'active' : 'inactive'); });
        panes.forEach(function (p) {
          var on = p.id === tabId;
          p.setAttribute('data-instant-state', on ? 'open' : 'closed');
          p.style.display = on ? '' : 'none';
        });
        // a slider inside the pane we just revealed has been dormant (clientWidth 0)
        setTimeout(safe(function () { allApis.forEach(function (a) { if (a.isDormant()) a.relayout(); }); }), 60);
      }
      triggers.forEach(function (t) {
        if (bound(t, '__mpdpTab')) return;
        t.addEventListener('click', safe(function (e) { e.preventDefault(); e.stopPropagation(); activate(t.getAttribute('data-instant-tab-id')); }));
      });
      var active = triggers.filter(function (t) { return t.getAttribute('data-instant-state') === 'active'; })[0] || triggers[0];
      activate(active.getAttribute('data-instant-tab-id'));
    });
  }

  /* ================= resize ================= */
  function initResize() {
    if (window.__mpdpResize) return;
    window.__mpdpResize = true;
    var t = null;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(safe(function () {
        allApis.forEach(function (s) { s.layout(); s.go(s.index(), false); });
        for (var k in mains) if (thumbs[k]) thumbs[k].follow(mains[k].index());
        $all('[data-instant-type="accordion-item"][data-state="open"]').forEach(function (item) { setAcc(item, true); });
      }), 100);
    });
  }

  /* ================= variant pickers =================
   * Instant's core does NOT bind these on our template. Verified live: clicking "Refurbished" left both
   * options' data-instant-state untouched, changed no URL and threw no error, while the same click on the
   * Instant-built page swapped the state and repointed the cart button. instant-core initialises the
   * sliders/tabs/accordions here but never the variant picker.
   *
   * So this module runs in BOTH modes, unlike everything else in this file — standing down would leave the
   * picker dead. It mirrors exactly what instant-core does on the Instant page:
   *   1. move data-instant-state between options sharing a data-instant-option-name
   *   2. match the selected values against the variants JSON the section already emits
   *   3. repoint every redirect-to-cart anchor (href + data-instant-action-variant-id)
   *   4. copy the chosen option's OWN rendered price markup into the button — the money string is never
   *      reformatted here, so it stays correct for any market, currency or money format.
   */
  function readJson(prefix) {
    var out = null;
    $all('script[type="application/json"]').forEach(function (s) {
      if (out || s.id.indexOf(prefix) !== 0) return;
      try { var v = JSON.parse(s.textContent); if (v && v.length) out = v; } catch (e) {}
    });
    return out;
  }

  /* Show the selected variant's own image in the hero gallery.
   *
   * Slide 0 of the gallery AND of its thumbnail strip is the selected variant's image, and the OTHER
   * variants' images are not rendered at all -- see the media loop in mpdp-hero.liquid. So a variant change
   * swaps that one image in place rather than navigating to it.
   *
   * It used to navigate, on the reasoning that both variant images were already slides so moving kept the
   * gallery and its thumbnails consistent. That was true and still produced a bad page: Shopify orders
   * variant media LAST, and Instant's sorted_media only hoists the one selected at page load, so the other
   * variant's shot was the final slide. Picking Refurbished threw you to the end of a 17-image strip.
   *
   * Scoped by CONTENT, not by a hashed slider id: a slider qualifies only if its first slide currently shows
   * one of this product's variant images. Other sections carry sliders too, and silently clobbering slide 0
   * of a reviews carousel is exactly what a hardcoded hash stops protecting against the day it changes.
   *
   * srcset is rewritten alongside src. A fresh src next to a stale srcset is the version of this bug that
   * appears only on some viewports, because the browser keeps choosing the old file from the srcset. Widths
   * are read back off the existing srcset so the large gallery image and the small thumbnail each keep their
   * own set rather than inheriting one hardcoded list.
   */
  function mediaNameOf(v) {
    var src = (v && v.featured_media && v.featured_media.preview_image && v.featured_media.preview_image.src) ||
              (v && v.featured_image && v.featured_image.src) || '';
    return src ? String(src).split('/').pop().split('?')[0] : '';
  }

  function showVariantHero(v, variants) {
    var src = (v && v.featured_media && v.featured_media.preview_image && v.featured_media.preview_image.src) ||
              (v && v.featured_image && v.featured_image.src) || '';
    if (!src) return false;

    var names = {};
    (variants || []).forEach(function (x) { var n = mediaNameOf(x); if (n) names[n] = 1; });
    if (!names[mediaNameOf(v)]) return false;

    var base = String(src).split(/[?&]width=/)[0];
    var sep = base.indexOf('?') >= 0 ? '&' : '?';
    var alt = (v.featured_media && v.featured_media.alt) || '';
    var hit = 0;

    $all('.instant-slider').forEach(function (el) {
      var slide = el.querySelector('.instant-slider-slide');
      var img = slide && (slide.querySelector('img.instant-image__main') || slide.querySelector('img'));
      if (!img) return;
      var cur = String(img.getAttribute('src') || '').split('/').pop().split('?')[0];
      if (!names[cur]) return;                       // not this product's gallery -- leave it alone
      hit++;

      var widths = (img.getAttribute('srcset') || '').split(',').map(function (part) {
        var m = String(part).trim().match(/\s(\d+)w$/);
        return m ? m[1] : null;
      }).filter(Boolean);
      if (widths.length) {
        img.setAttribute('srcset', widths.map(function (w) { return base + sep + 'width=' + w + ' ' + w + 'w'; }).join(', '));
      }
      var cw = (img.getAttribute('src') || '').match(/[?&]width=(\d+)/);
      img.setAttribute('src', base + sep + 'width=' + (cw ? cw[1] : '1280'));
      if (alt) img.setAttribute('alt', alt);

      // Bring the swap on screen if the reader had paged away. Only the main slider is driven: in both modes
      // moving it takes its thumbnail strip along, while driving the strip directly leaves the gallery put.
      if (el.classList.contains('instant-slider-thumbs')) return;
      if (el.swiper) { try { el.swiper.slideTo(0); } catch (e) {} }
      else if (el.__mpdpSlider) { try { el.__mpdpSlider.go(0, true); } catch (e) {} }
    });

    return hit > 0;
  }

  /* No instalment updater here on purpose. Both quoted per-instalment prices are pinned to the
   * cheapest variant in mpdp-hero.liquid, because the split-payments line renders ABOVE the picker
   * and directly under a static "From $599". A previous version rewrote them on variant change;
   * that put "From $599" next to "$116.50" with nothing on screen to explain the gap. If you add
   * one back, read the comment at the top of mpdp-hero.liquid first. */

  function initVariantPickers() {
    var pickers = $all('[data-instant-action-type="select-variant-option"]');
    if (!pickers.length) return;
    var variants = readJson('variants__');
    var options = readJson('options__');
    if (!variants || !options) return;

    var posByName = {};
    options.forEach(function (o) { posByName[o.name] = o.position || 1; });

    function selected() {
      var vals = {};
      pickers.forEach(function (p) {
        if (p.getAttribute('data-instant-state') === 'active') vals[p.getAttribute('data-instant-option-name')] = p.getAttribute('data-instant-option-value');
      });
      return vals;
    }

    function match(vals) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i], ok = true;
        for (var name in vals) { if (v['option' + (posByName[name] || 1)] !== vals[name]) { ok = false; break; } }
        if (ok) return v;
      }
      return null;
    }

    function apply(picker) {
      var name = picker.getAttribute('data-instant-option-name');
      pickers.forEach(function (p) {
        if (p.getAttribute('data-instant-option-name') !== name) return;
        p.setAttribute('data-instant-state', p === picker ? 'active' : 'inactive');
      });
      var v = match(selected());
      if (!v) return;
      var priceSrc = picker.querySelector('[data-instant-dynamic-content-source="PRICE"]');
      $all('[data-instant-action-type="redirect-to-cart"]').forEach(function (a) {
        a.setAttribute('data-instant-action-variant-id', String(v.id));
        a.setAttribute('href', '/cart/' + v.id + ':1?storefront=true');
        var tgt = a.querySelector('[data-instant-dynamic-content-source="PRICE"]');
        if (tgt && priceSrc) tgt.innerHTML = priceSrc.innerHTML;
      });
      safe(showVariantHero)(v, variants);
    }

    pickers.forEach(function (p) {
      if (bound(p, '__mpdpVariantBound')) return;
      p.addEventListener('click', safe(function () {
        if (p.getAttribute('data-instant-disabled') === 'true') return;
        apply(p);
      }));
    });
  }

  /* ================= Videowise testimonial cards ================= */
  // The "Discover Testimonials" carousel in how-it-works is a Videowise widget that renders into a CLOSED shadow
  // root, so theme CSS cannot reach it. The Videowise client does register every root it creates on
  // window.videowiseShadowRoots (keyed by widget id), and its dashboard writes the card title's alignment as an
  // INLINE style (text-align:center) — so the override must be !important and must live INSIDE the root.
  // @font-face is document-scoped, so the theme's "Avenir Web" (400 = Book, 700 = Heavy) resolves in the shadow
  // tree. Only widgets mounted inside an mpdp section are touched; both modes (Instant live or blocked). If
  // Videowise ever drops the registry nothing happens — the widget simply keeps its own styling.
  var VW_CSS = '.vw-cmp__in-video-card--title,.vw-cmp__in-video-card--description{text-align:left!important;' +
    'font-family:"Avenir Web",-apple-system,system-ui,"Segoe UI",Roboto,Helvetica,Arial,sans-serif!important}';
  function styleVideowiseRoot(root) {
    if (!root || !root.host || root.__mpdpStyled) return;
    if (!closest(root.host, ROOT_SEL)) return;
    root.__mpdpStyled = true;
    var s = document.createElement('style');
    s.setAttribute('data-mpdp-videowise', '');
    s.textContent = VW_CSS;
    root.appendChild(s);
    // The client empties the root when it re-initialises a widget — put the sheet back.
    try { new MutationObserver(function () { if (!s.parentNode) root.appendChild(s); }).observe(root, { childList: true }); } catch (e) {}
  }
  function initVideowiseText() {
    var tries = 0;
    (function poll() {
      var reg = window.videowiseShadowRoots;
      if (reg) for (var k in reg) safe(styleVideowiseRoot)(reg[k]);
      // Widgets mount lazily as they scroll into view: 250ms for the first minute, then once a second.
      setTimeout(poll, ++tries < 240 ? 250 : 1000);
    })();
  }

  /* ================= mode decision ================= */
  // Evidence that instant-core ITSELF ran (never window.Instant.initialized — the app embed sets that inline).
  function coreRan() {
    var I = window.Instant;
    if (I && (typeof I.initSwiper === 'function' || I.initializedListeners || I.eventAbortController || I.api)) return true;
    return !!document.querySelector('[data-instant-slider-initialized="true"]');
  }
  function standDown() { window.__mpdpMode = 'instant'; }
  function takeOver() {
    window.__mpdpMode = 'native';
    $all(ROOT_SEL).forEach(function (root) {
      safe(initSliders)(root);
      safe(initAccordions)(root);
      safe(initOverlays)(root);
      safe(initTabs)(root);
      safe(initScrollTo)(root);
      safe(initScrollTriggers)(root);
      safe(initTickers)(root);
    });
    safe(initResize)();
    window.__mpdpNativeReady = true;
    try { window.dispatchEvent(new Event('mpdp:ready')); } catch (e) {}
  }
  function decide() {
    if (coreRan()) return standDown();
    if (!document.querySelector('script[src*="instant-core"]')) return takeOver();
    var tries = 0;
    (function poll() {
      if (coreRan()) return standDown();
      if (++tries > 15) return takeOver();   // ~1.5s: the tag is present but blocked/failed
      setTimeout(poll, 100);
    })();
  }

  // This tag is `defer` and sits after the app embed's instant-core tag, so by now instant-core has run if it loaded.
  // Variant pickers bind in BOTH modes (see above) — instant-core never wires them on this template.
  safe(initVariantPickers)();
  safe(initVideowiseText)();
  decide();
})();
